import { Recipe, Meal } from '../types';
import dayjs from 'dayjs';

// Common ingredients that don't indicate meal similarity
const COMMON_INGREDIENTS = [
  'salt',
  'pepper',
  'black pepper',
  'white pepper',
  'oil',
  'olive oil',
  'vegetable oil',
  'canola oil',
  'butter',
  'water',
  'garlic',
  'onion',
  'onions',
  'flour',
  'all-purpose flour',
  'sugar',
  'brown sugar',
  'white sugar',
  'egg',
  'eggs',
  'milk',
  'tomato',
  'tomatoes',
  'lemon',
  'lime',
  'parsley',
  'basil',
  'oregano',
  'thyme',
  'bay leaf',
  'bay leaves',
];

// Stop words to ignore in recipe name comparison
const STOP_WORDS = [
  'with',
  'and',
  'or',
  'the',
  'a',
  'an',
  'in',
  'on',
  'at',
  'to',
  'for',
  'of',
  'by',
  'from',
  'as',
  'is',
  'was',
  'are',
  'were',
];

export interface PlannedMeal {
  recipe: Recipe;
  date: Date;
}

/**
 * Generate a meal plan for the specified date range
 */
export function generateMealPlan(
  recipes: Recipe[],
  existingMeals: Meal[],
  startDate: Date,
  endDate: Date
): PlannedMeal[] {
  const numberOfDays = dayjs(endDate).diff(dayjs(startDate), 'day') + 1;

  if (numberOfDays <= 0 || numberOfDays > 10) {
    throw new Error('Date range must be between 1 and 10 days');
  }

  if (recipes.length === 0) {
    throw new Error('No recipes available');
  }

  const plan: { recipe: Recipe; date: Date }[] = [];
  const availableRecipes = [...recipes];

  for (let i = 0; i < numberOfDays; i++) {
    const currentDate = dayjs(startDate).add(i, 'day').toDate();

    // Calculate probabilities for all remaining recipes
    const recipesWithProbabilities = availableRecipes.map((recipe) => ({
      recipe,
      probability: calculateRecipeProbability(recipe, existingMeals, plan.map((p) => p.recipe)),
    }));

    if (recipesWithProbabilities.length === 0) {
      throw new Error('Not enough recipes for the selected date range');
    }

    // Select recipe using weighted random based on probabilities
    const selectedRecipe = selectByProbability(recipesWithProbabilities);

    plan.push({
      recipe: selectedRecipe,
      date: currentDate,
    });

    // Remove selected recipe from available pool
    const index = availableRecipes.findIndex((r) => r.recipeId === selectedRecipe.recipeId);
    if (index > -1) {
      availableRecipes.splice(index, 1);
    }
  }

  return plan;
}

/**
 * Calculate selection probability for a recipe
 * Uses multipliers rather than penalties to ensure all recipes remain viable
 */
function calculateRecipeProbability(
  recipe: Recipe,
  existingMeals: Meal[],
  selectedRecipes: Recipe[]
): number {
  // Start with base probability
  let probability = 1.0;

  // Usage multiplier: favor less-used recipes
  // Range: 0.5x (very popular) to 2.0x (rarely used)
  const usageCount = recipe.timesAddedAsMeal || 0;
  const usageMultiplier = 2.0 / (1 + Math.log(usageCount + 1));
  probability *= usageMultiplier;

  // Recency multiplier: reduce recently used recipes
  const recencyMultiplier = getRecencyMultiplier(recipe, existingMeals);
  probability *= recencyMultiplier;

  // Diversity multiplier: reduce similar recipes
  const diversityMultiplier = getDiversityMultiplier(recipe, selectedRecipes);
  probability *= diversityMultiplier;

  // Random variation: ±20% to ensure different results each generation
  const randomMultiplier = 0.9 + Math.random() * 0.2; // Range: 0.9x to 1.1x
  probability *= randomMultiplier;

  return probability;
}

/**
 * Calculate recency multiplier (never reaches 0)
 * Range: 0.2x (used yesterday) to 1.0x (not used in 30+ days)
 */
function getRecencyMultiplier(recipe: Recipe, existingMeals: Meal[]): number {
  const recentMeals = existingMeals.filter((meal) => {
    if (meal.recipeId !== recipe.recipeId) return false;
    const daysSince = dayjs().diff(dayjs(meal.date), 'day');
    return daysSince <= 30; // Extended from 14 to 30 days
  });

  if (recentMeals.length === 0) return 1.0; // No penalty

  // Find most recent usage
  const daysSinceLastUse = Math.min(
    ...recentMeals.map((meal) => dayjs().diff(dayjs(meal.date), 'day'))
  );

  // Smooth curve: 0 days = 0.2x, 30 days = 1.0x
  return 0.2 + (daysSinceLastUse / 30) * 0.8;
}

/**
 * Calculate diversity multiplier based on similarity to already-selected recipes
 * Range: 0.3x (very similar) to 1.0x (completely different)
 */
function getDiversityMultiplier(recipe: Recipe, selectedRecipes: Recipe[]): number {
  if (selectedRecipes.length === 0) return 1.0;

  let lowestMultiplier = 1.0;

  for (const selected of selectedRecipes) {
    let recipeMultiplier = 1.0;

    // Name similarity
    const nameSimilarity = calculateNameSimilarity(recipe.name, selected.name);
    if (nameSimilarity > 0.7) {
      recipeMultiplier *= 0.5; // 50% less likely
    }

    // Label overlap
    const labelOverlap = calculateOverlap(recipe.labels || [], selected.labels || []);
    if (labelOverlap > 0.5) {
      recipeMultiplier *= 0.6; // 40% less likely
    }

    // Ingredient overlap
    const sigIngredients1 = getSignificantIngredients(recipe);
    const sigIngredients2 = getSignificantIngredients(selected);
    const ingredientOverlap = calculateOverlap(sigIngredients1, sigIngredients2);
    if (ingredientOverlap > 0.6) {
      recipeMultiplier *= 0.5; // 50% less likely
    }

    // Track the lowest multiplier (most similar recipe)
    lowestMultiplier = Math.min(lowestMultiplier, recipeMultiplier);
  }

  // Ensure minimum 30% probability even for very similar recipes
  return Math.max(lowestMultiplier, 0.3);
}

/**
 * Select a recipe using weighted random selection based on probabilities
 * Every recipe has a chance proportional to its probability
 */
function selectByProbability(
  recipes: { recipe: Recipe; probability: number }[]
): Recipe {
  // Calculate total probability
  const totalProbability = recipes.reduce((sum, r) => sum + r.probability, 0);

  // Select random point in probability range
  let random = Math.random() * totalProbability;

  // Find selected recipe
  for (const item of recipes) {
    random -= item.probability;
    if (random <= 0) {
      return item.recipe;
    }
  }

  // Fallback to first recipe (should never reach here)
  return recipes[0].recipe;
}

/**
 * Get significant ingredients (excluding common ones)
 */
function getSignificantIngredients(recipe: Recipe): string[] {
  return recipe.ingredients
    .map((i) => i.name.toLowerCase().trim())
    .filter((name) => {
      // Check if ingredient is in common list
      return !COMMON_INGREDIENTS.some(
        (common) => name.includes(common) || common.includes(name)
      );
    });
}

/**
 * Calculate name similarity using Levenshtein distance with stop-word filtering
 */
function calculateNameSimilarity(name1: string, name2: string): number {
  // Remove stop words and normalize
  const clean1 = removeStopWords(name1.toLowerCase());
  const clean2 = removeStopWords(name2.toLowerCase());

  if (clean1.length === 0 || clean2.length === 0) return 0;

  const distance = levenshteinDistance(clean1, clean2);
  const maxLength = Math.max(clean1.length, clean2.length);

  // Convert distance to similarity (0 = different, 1 = identical)
  return 1 - distance / maxLength;
}

/**
 * Remove stop words from text
 */
function removeStopWords(text: string): string {
  const words = text.split(/\s+/);
  const filtered = words.filter((word) => !STOP_WORDS.includes(word.toLowerCase()));
  return filtered.join(' ');
}

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const m = str1.length;
  const n = str2.length;
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]) + 1;
      }
    }
  }

  return dp[m][n];
}

/**
 * Calculate overlap percentage between two arrays
 */
function calculateOverlap(arr1: string[], arr2: string[]): number {
  if (arr1.length === 0 || arr2.length === 0) return 0;

  const set1 = new Set(arr1.map((s) => s.toLowerCase()));
  const set2 = new Set(arr2.map((s) => s.toLowerCase()));

  let matches = 0;
  set1.forEach((item) => {
    if (set2.has(item)) matches++;
  });

  // Use smaller set size as denominator for stricter comparison
  return matches / Math.min(set1.size, set2.size);
}

/**
 * Find a replacement recipe for a specific slot in the plan
 * @param currentRecipe The recipe to replace
 * @param allRecipes All available recipes
 * @param selectedRecipes Other recipes already in the plan (excluding the one being replaced)
 * @param existingMeals Previously added meals (for recency check)
 * @param excludeRecipeIds Recipe IDs to exclude from consideration
 * @returns A replacement recipe, or null if no suitable replacement found
 */
export function findReplacementRecipe(
  currentRecipe: Recipe,
  allRecipes: Recipe[],
  selectedRecipes: Recipe[],
  existingMeals: Meal[],
  excludeRecipeIds: string[] = []
): Recipe | null {
  // Filter out the current recipe and any excluded recipes
  const candidateRecipes = allRecipes.filter((recipe) => {
    if (recipe.recipeId === currentRecipe.recipeId) return false;
    if (excludeRecipeIds.includes(recipe.recipeId || '')) return false;
    return true;
  });

  if (candidateRecipes.length === 0) return null;

  // Calculate probabilities for all candidate recipes
  const recipesWithProbabilities = candidateRecipes.map((recipe) => ({
    recipe,
    probability: calculateRecipeProbability(recipe, existingMeals, selectedRecipes),
  }));

  // Select using probability-based selection
  return selectByProbability(recipesWithProbabilities);
}
